import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import { ArrowPathIcon, CalendarIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import { ActionType, maxGoalUpsertMarketers, RegionId, UserId } from "@sp-crm/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Stage } from "components/layout";
import { monthSpanLabel, monthStartLabel } from "components/marketing-goals/date-range";
import {
    addGoalDraftColumn,
    fillGoalDraftColumn,
    goalApplyMonthOptions,
    goalColumnKeysForRows,
    GoalDraft,
    goalDraftChangedMarketerIds,
    goalDraftFromRows,
    goalDraftMarketerIds,
    goalDraftSaveGroups,
    goalDraftValue,
    goalGridColumns,
    goalMarketerOptionIds,
    goalRowsWithTargets,
    MarketerGoalRow,
    marketerGoalRowsForMonth,
    parseGoalTarget,
    removeGoalDraftColumn,
    scheduledGoalMarketerIds,
    scheduledGoalMonths,
    setGoalDraftMarketers,
    setGoalDraftValue,
    uniformGoalDraftValue,
} from "components/marketing-goals/goal-grid";
import {
    editGoalsPath,
    isGoalMonth,
    manageGoalsPath,
    marketerGoalsPath,
    newGoalsPath,
} from "components/marketing-goals/goal-routes";
import {
    buildTargetRowSections,
    flattenTargets,
    TargetRowSection,
} from "components/marketing-goals/goal-target-rows";
import {
    GoalVersionLike,
    versionTargets,
} from "components/marketing-goals/goal-versions";
import { currentYearMonth, ymAddMonths, ymLabel } from "components/marketing-goals/pace";
import { SharedTargetInput } from "components/marketing-goals/shared-target-input";
import { trpc } from "components/rpc/trpc";
import { useActivityTypeOptionsState } from "components/tasks/activity-type-options";
import { DeleteButton } from "components/ui/action-button";
import { Badge } from "components/ui/badge";
import {
    PrimaryButtonLink,
    SecondaryButtonLink,
    SecondaryCompactButtonLink,
} from "components/ui/button-link";
import { ButtonSelect } from "components/ui/button-select";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { PrimaryButton } from "components/ui/primary-button";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Select } from "components/ui/select";
import { SpMultiSelect } from "components/ui/sp-multi-select";
import { Spinner } from "components/ui/spinner";
import React, { useMemo, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router";
import {
    useCrmMode,
    useIsAllowed,
    useNow,
    useRegionId,
    useUsersForRegion,
} from "store/selectors/hooks";
import { UserMap } from "store/selectors/users";
import { stableQueryOptionsRpc } from "util/requests";

const monthsAhead = 6;

const marketerName = (users: UserMap, marketerUserId: UserId): string =>
    users[marketerUserId]?.name || "Unknown user";

interface GoalsReadoutProps {
    rows: MarketerGoalRow[];
    sections: TargetRowSection[];
    userName: (marketerUserId: UserId) => string;
}

const GoalsReadout: React.FC<GoalsReadoutProps> = ({ rows, sections, userName }) => {
    const columns = goalGridColumns(goalColumnKeysForRows(rows), sections);
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full">
                <thead>
                    <tr className="border-b border-zinc-100 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        <th scope="col" className="w-full px-4 py-1.5">
                            Marketer
                        </th>
                        {columns.map(column => {
                            const ColumnIcon = column.icon;
                            return (
                                <th
                                    key={column.key}
                                    scope="col"
                                    className="whitespace-nowrap px-3 py-1.5">
                                    <span className="flex items-center gap-1.5">
                                        <ColumnIcon
                                            aria-hidden="true"
                                            className="size-3.5 flex-shrink-0 text-gray-400"
                                        />
                                        <span>{column.label}</span>
                                    </span>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50">
                    {rows.map(row => {
                        const targets = flattenTargets(row.targets);
                        return (
                            <tr key={row.marketerUserId}>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                    {userName(row.marketerUserId)}
                                </td>
                                {columns.map(column => (
                                    <td
                                        key={column.key}
                                        className="px-3 py-2 text-sm font-semibold tabular-nums text-gray-900">
                                        {targets[column.key] > 0 ? (
                                            targets[column.key]
                                        ) : (
                                            <span className="font-normal text-gray-300">
                                                —
                                            </span>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

interface GoalMetricChipsProps {
    sections: TargetRowSection[];
    columnKeys: string[];
    onToggle: (key: string) => void;
}

const GoalMetricChips: React.FC<GoalMetricChipsProps> = ({
    sections,
    columnKeys,
    onToggle,
}) => (
    <div className="space-y-1">
        {sections.map(section => {
            const SectionIcon = section.icon;
            return (
                <div key={section.title} className="flex flex-wrap items-start gap-x-2">
                    <div className="my-2 flex w-20 flex-shrink-0 items-center gap-1 py-1">
                        <SectionIcon
                            aria-hidden="true"
                            className="h-4 w-4 flex-shrink-0 text-gray-500"
                        />
                        <span className="whitespace-nowrap text-xs font-medium text-gray-500">
                            {section.title}
                        </span>
                    </div>
                    <div className="min-w-0 flex-1">
                        <ButtonSelect
                            options={section.rows.map(row => ({
                                id: row.key,
                                label: row.label,
                            }))}
                            selectedIds={columnKeys}
                            onToggle={onToggle}
                        />
                    </div>
                </div>
            );
        })}
    </div>
);

const GoalMetricPicker: React.FC<GoalMetricChipsProps> = ({
    sections,
    columnKeys,
    onToggle,
}) => {
    const nothingChosen = columnKeys.length === 0;
    return (
        <div
            className={`rounded-t-lg px-4 pb-1 pt-3 ${nothingChosen ? "bg-brand-100" : ""}`}>
            <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">
                    Choose which activities to track as goals
                </h3>
                {nothingChosen ? <Badge type="highlight" value="Start here" /> : null}
            </div>
            <p className="mt-0.5 text-sm text-gray-500">
                Select the activity types and outcomes to include as goals. Unselect an
                activity type to remove the associated goal.
            </p>
            <GoalMetricChips
                sections={sections}
                columnKeys={columnKeys}
                onToggle={onToggle}
            />
        </div>
    );
};

interface GoalGridProps {
    draft: GoalDraft;
    setDraft: React.Dispatch<React.SetStateAction<GoalDraft>>;
    sections: TargetRowSection[];
    marketerUserIds: UserId[];
    deactivatedMarketerUserIds: UserId[];
    userName: (marketerUserId: UserId) => string;
}

const GoalGrid: React.FC<GoalGridProps> = ({
    draft,
    setDraft,
    sections,
    marketerUserIds,
    deactivatedMarketerUserIds,
    userName,
}) => {
    const columns = goalGridColumns(draft.columnKeys, sections);
    const editedMarketerIds = goalDraftMarketerIds(draft, marketerUserIds);
    const marketerOptions = goalMarketerOptionIds(
        draft,
        marketerUserIds,
        deactivatedMarketerUserIds,
    ).map(marketerUserId => ({
        id: marketerUserId,
        label: userName(marketerUserId),
    }));

    const toggleColumn = (key: string) =>
        setDraft(current =>
            current.columnKeys.includes(key)
                ? removeGoalDraftColumn(current, key)
                : addGoalDraftColumn(current, key),
        );

    const fillColumn = (key: string, target: number) =>
        setDraft(current => fillGoalDraftColumn(current, editedMarketerIds, key, target));

    return (
        <div className="rounded-lg bg-white ring-1 ring-zinc-200">
            <GoalMetricPicker
                sections={sections}
                columnKeys={draft.columnKeys}
                onToggle={toggleColumn}
            />
            <div className="border-t border-zinc-100 px-4 py-3 text-sm">
                <div className="max-w-xl">
                    <SpMultiSelect
                        label="Marketers"
                        options={marketerOptions}
                        value={editedMarketerIds}
                        onChange={chosen =>
                            setDraft(current => setGoalDraftMarketers(current, chosen))
                        }
                        placeholder="Choose marketers…"
                    />
                </div>
            </div>
            <div className="overflow-x-auto rounded-b-lg border-t border-zinc-100">
                <table className="min-w-full">
                    <thead>
                        <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            <th scope="col" className="w-full px-4 py-2">
                                Marketer
                            </th>
                            {columns.map(column => {
                                const ColumnIcon = column.icon;
                                return (
                                    <th
                                        key={column.key}
                                        scope="col"
                                        className="whitespace-nowrap px-3 py-2">
                                        <span className="flex items-center gap-1.5">
                                            <ColumnIcon
                                                aria-hidden="true"
                                                className="size-3.5 flex-shrink-0 text-gray-400"
                                            />
                                            <span>{column.label}</span>
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                        {editedMarketerIds.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={columns.length + 1}
                                    className="px-4 py-6 text-center text-sm text-gray-500">
                                    No marketers yet. Choose them above.
                                </td>
                            </tr>
                        ) : null}
                        {editedMarketerIds.length > 1 && columns.length > 0 ? (
                            <tr className="bg-zinc-50">
                                <td className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Everyone
                                </td>
                                {columns.map(column => {
                                    const shared = uniformGoalDraftValue(
                                        draft,
                                        editedMarketerIds,
                                        column.key,
                                    );
                                    return (
                                        <td key={column.key} className="px-3 py-2">
                                            <div className="w-20">
                                                <SharedTargetInput
                                                    columnLabel={column.label}
                                                    shared={shared}
                                                    onChange={target =>
                                                        fillColumn(column.key, target)
                                                    }
                                                />
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ) : null}
                        {editedMarketerIds.map(marketerUserId => (
                            <tr key={marketerUserId}>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                    {userName(marketerUserId)}
                                </td>
                                {columns.map(column => {
                                    const value = goalDraftValue(
                                        draft,
                                        marketerUserId,
                                        column.key,
                                    );
                                    return (
                                        <td key={column.key} className="px-3 py-2">
                                            <div className="w-20">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    textSize="sm"
                                                    aria-label={`${column.label} goal for ${userName(marketerUserId)}`}
                                                    value={value ? String(value) : ""}
                                                    onChange={event =>
                                                        setDraft(current =>
                                                            setGoalDraftValue(
                                                                current,
                                                                marketerUserId,
                                                                column.key,
                                                                parseGoalTarget(
                                                                    event.target.value,
                                                                ),
                                                            ),
                                                        )
                                                    }
                                                />
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

interface GoalDraftViewProps {
    regionId: RegionId;
    versions: GoalVersionLike[];
    marketerUserIds: UserId[];
    deactivatedMarketerUserIds: UserId[];
    sections: TargetRowSection[];
    month: string;
    monthControl: React.ReactNode;
    userName: (marketerUserId: UserId) => string;
}

const GoalDraftView: React.FC<GoalDraftViewProps> = ({
    regionId,
    versions,
    marketerUserIds,
    deactivatedMarketerUserIds,
    sections,
    month,
    monthControl,
    userName,
}) => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const history = useHistory();
    const rows = useMemo(
        () => marketerGoalRowsForMonth(versions, marketerUserIds, month),
        [versions, marketerUserIds, month],
    );
    const [draft, setDraft] = useState<GoalDraft>(() => goalDraftFromRows(rows));
    const [saveError, setSaveError] = useState<string | null>(null);

    const saveMutation = useMutation({
        ...trpcClient.marketingGoal.upsertGroups.mutationOptions(),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: trpcClient.marketingGoal.teamVersions.queryKey(),
            });
            await queryClient.invalidateQueries({
                queryKey: trpcClient.marketingGoal.teamAttainmentForRange.queryKey(),
            });
            history.push(manageGoalsPath);
        },
        onError: () => setSaveError("Couldn't save goals. Please try again."),
    });

    const changed = goalDraftChangedMarketerIds(rows, draft);
    const tooManyChanges = changed.length > maxGoalUpsertMarketers;

    const handleSave = () => {
        if (changed.length === 0 || tooManyChanges || saveMutation.isPending) {
            return;
        }
        setSaveError(null);
        saveMutation.mutate({
            regionId,
            effectiveMonth: month,
            groups: goalDraftSaveGroups(rows, draft),
        });
    };

    return (
        <div className="space-y-6">
            {monthControl}

            <GoalGrid
                draft={draft}
                setDraft={setDraft}
                sections={sections}
                marketerUserIds={marketerUserIds}
                deactivatedMarketerUserIds={deactivatedMarketerUserIds}
                userName={userName}
            />

            {tooManyChanges ? (
                <InlineBanner type="warning">
                    Goals can be saved for up to {maxGoalUpsertMarketers} marketers at a
                    time.
                </InlineBanner>
            ) : null}

            {saveError ? <InlineBanner type="error">{saveError}</InlineBanner> : null}

            <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4">
                <SecondaryButtonLink to={manageGoalsPath}>Cancel</SecondaryButtonLink>
                <PrimaryButton
                    disabled={
                        changed.length === 0 || tooManyChanges || saveMutation.isPending
                    }
                    onClick={handleSave}>
                    Save goals
                </PrimaryButton>
            </div>
        </div>
    );
};

interface ApplyStartingMonthProps {
    versions: GoalVersionLike[];
    firstMonth: string;
    month: string;
}

const ApplyStartingMonth: React.FC<ApplyStartingMonthProps> = ({
    versions,
    firstMonth,
    month,
}) => {
    const history = useHistory();
    return (
        <div className="w-64">
            <Select
                textSize="sm"
                label="Apply starting"
                value={month}
                onChange={event => history.replace(newGoalsPath(event.target.value))}>
                {goalApplyMonthOptions(versions, firstMonth, monthsAhead).map(option => (
                    <option key={option} value={option}>
                        {monthStartLabel(option)}
                    </option>
                ))}
            </Select>
        </div>
    );
};

interface ScheduledGoalsCardProps {
    regionId: RegionId;
    month: string;
    rows: MarketerGoalRow[];
    sections: TargetRowSection[];
    userName: (marketerUserId: UserId) => string;
    scheduledMarketerUserIds: UserId[];
}

const ScheduledGoalsCard: React.FC<ScheduledGoalsCardProps> = ({
    regionId,
    month,
    rows,
    sections,
    userName,
    scheduledMarketerUserIds,
}) => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const [removeError, setRemoveError] = useState<string | null>(null);
    const removeMutation = useMutation({
        ...trpcClient.marketingGoal.deleteScheduled.mutationOptions(),
        onSuccess: async () => {
            setRemoveError(null);
            await queryClient.invalidateQueries({
                queryKey: trpcClient.marketingGoal.teamVersions.queryKey(),
            });
            await queryClient.invalidateQueries({
                queryKey: trpcClient.marketingGoal.teamAttainmentForRange.queryKey(),
            });
        },
        onError: () => setRemoveError("Couldn't remove these goals. Please try again."),
    });

    return (
        <div>
            <div className="overflow-hidden rounded-lg bg-white ring-1 ring-zinc-200">
                <div className="flex items-center gap-1.5 border-b border-zinc-100 px-4 py-2.5">
                    <CalendarIcon aria-hidden="true" className="size-4 text-gray-500" />
                    <span className="text-sm font-semibold text-gray-900">
                        Starts {monthStartLabel(month)}
                    </span>
                </div>
                {rows.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-600">No goals.</div>
                ) : (
                    <GoalsReadout rows={rows} sections={sections} userName={userName} />
                )}
                <div className="flex items-center justify-end gap-2.5 border-t border-zinc-100 bg-zinc-50/60 px-4 py-2.5">
                    <SecondaryCompactButtonLink to={editGoalsPath(month)}>
                        Edit
                    </SecondaryCompactButtonLink>
                    <DeleteButton
                        backgroundColor="bg-white"
                        disabled={removeMutation.isPending}
                        confirm={{
                            title: "Remove scheduled goals?",
                            message: `Goals stay as they are today instead of changing on ${monthStartLabel(month)}.`,
                            confirmLabel: "Yes, remove",
                        }}
                        onClick={() =>
                            removeMutation.mutate({
                                regionId,
                                effectiveMonth: month,
                                marketerUserIds: scheduledMarketerUserIds,
                            })
                        }
                    />
                </div>
            </div>
            {removeError ? (
                <div className="mt-2">
                    <InlineBanner type="error">{removeError}</InlineBanner>
                </div>
            ) : null}
        </div>
    );
};

interface ManageGoalsData {
    regionId: RegionId;
    versions: GoalVersionLike[];
    marketerUserIds: UserId[];
    deactivatedMarketerUserIds: UserId[];
    sections: TargetRowSection[];
    userName: (marketerUserId: UserId) => string;
}

interface ManageGoalsPageProps {
    title: string;
    backTo: string;
    backLabel: string;
    children: (data: ManageGoalsData) => React.ReactNode;
}

const ManageGoalsPage: React.FC<ManageGoalsPageProps> = ({
    title,
    backTo,
    backLabel,
    children,
}) => {
    const regionId = useRegionId();
    const effectiveRegionId = regionId ?? ("" as RegionId);
    const canManage = useIsAllowed(ActionType.UpdateRegionSettings);
    const { mode: crmMode } = useCrmMode();
    const trpcClient = trpc.useTRPC();
    const users = useUsersForRegion(effectiveRegionId);
    const { options: activityTypeOptions, isLoading: optionsLoading } =
        useActivityTypeOptionsState();

    const teamQuery = useQuery({
        ...trpcClient.marketingGoal.teamVersions.queryOptions({
            regionId: effectiveRegionId,
        }),
        ...stableQueryOptionsRpc(),
        enabled: canManage && !!regionId,
    });

    const versions = useMemo(() => teamQuery.data?.versions ?? [], [teamQuery.data]);

    const marketerUserIds = useMemo(
        () =>
            [...(teamQuery.data?.marketerUserIds ?? [])].sort((a, b) =>
                marketerName(users, a).localeCompare(marketerName(users, b)),
            ),
        [teamQuery.data, users],
    );

    const deactivatedMarketerUserIds = useMemo(
        () => marketerUserIds.filter(marketerUserId => !!users[marketerUserId]?.disabled),
        [marketerUserIds, users],
    );

    const sections = useMemo(
        () =>
            buildTargetRowSections(
                activityTypeOptions,
                crmMode,
                versions.map(versionTargets),
            ),
        [activityTypeOptions, crmMode, versions],
    );

    const userName = (marketerUserId: UserId): string =>
        marketerName(users, marketerUserId);

    return (
        <Stage>
            <PageHeader title={title} stackBreakpoint="md" />
            <div className="min-h-full bg-gray-50 px-4 py-4 lg:px-6">
                <div className="mb-4">
                    <SecondaryCompactButtonLink to={backTo}>
                        <ChevronLeftIcon className="size-4" /> {backLabel}
                    </SecondaryCompactButtonLink>
                </div>
                {!canManage ? (
                    <div className="py-12 text-center text-sm text-gray-600">
                        You need office administrator access for this office to manage
                        marketer goals.
                    </div>
                ) : teamQuery.isLoading || optionsLoading ? (
                    <div className="flex justify-center py-12">
                        <Spinner />
                    </div>
                ) : teamQuery.isError ? (
                    <RetryableErrorBanner
                        message="Couldn't load goals."
                        onRetry={() => void teamQuery.refetch()}
                        retrying={teamQuery.isFetching}
                    />
                ) : marketerUserIds.length === 0 ? (
                    <div className="rounded-lg bg-white px-4 py-10 text-center text-sm text-gray-600 ring-1 ring-zinc-200">
                        This office has no marketers to set goals for yet.
                    </div>
                ) : (
                    children({
                        regionId: effectiveRegionId,
                        versions,
                        marketerUserIds,
                        deactivatedMarketerUserIds,
                        sections,
                        userName,
                    })
                )}
            </div>
        </Stage>
    );
};

interface GoalScheduleListProps {
    data: ManageGoalsData;
    currentMonth: string;
}

const GoalScheduleList: React.FC<GoalScheduleListProps> = ({ data, currentMonth }) => {
    const { regionId, versions, marketerUserIds, sections, userName } = data;

    const currentRows = useMemo(
        () =>
            goalRowsWithTargets(
                marketerGoalRowsForMonth(versions, marketerUserIds, currentMonth),
            ),
        [versions, marketerUserIds, currentMonth],
    );

    const upcomingMonths = useMemo(
        () => scheduledGoalMonths(versions, currentMonth),
        [versions, currentMonth],
    );

    const nextMonth = ymAddMonths(currentMonth, 1);

    return (
        <div className="space-y-6">
            {currentRows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center">
                    <ChartBarIcon
                        aria-hidden="true"
                        className="mx-auto size-8 text-gray-400"
                    />
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">
                        No goals yet
                    </h3>
                    <div className="mt-4 flex justify-center">
                        <PrimaryButtonLink to={editGoalsPath(currentMonth)}>
                            Set goals
                        </PrimaryButtonLink>
                    </div>
                </div>
            ) : (
                <div>
                    <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Current goals
                    </h3>
                    <div className="overflow-hidden rounded-lg bg-white ring-1 ring-zinc-200">
                        <div className="border-b border-zinc-100 px-4 py-2.5 text-sm font-semibold text-gray-900">
                            {ymLabel(currentMonth)}{" "}
                            <span className="font-normal text-gray-500">
                                · {monthSpanLabel(currentMonth)}
                            </span>
                        </div>
                        <GoalsReadout
                            rows={currentRows}
                            sections={sections}
                            userName={userName}
                        />
                        <div className="flex justify-end border-t border-zinc-100 bg-zinc-50/60 px-4 py-2.5">
                            <SecondaryCompactButtonLink to={editGoalsPath(currentMonth)}>
                                Edit
                            </SecondaryCompactButtonLink>
                        </div>
                    </div>
                </div>
            )}

            {currentRows.length > 0 || upcomingMonths.length > 0 ? (
                <div>
                    <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Upcoming
                    </h3>
                    <div className="space-y-3">
                        {upcomingMonths.map(month => (
                            <ScheduledGoalsCard
                                key={month}
                                regionId={regionId}
                                month={month}
                                rows={goalRowsWithTargets(
                                    marketerGoalRowsForMonth(
                                        versions,
                                        marketerUserIds,
                                        month,
                                    ),
                                )}
                                sections={sections}
                                userName={userName}
                                scheduledMarketerUserIds={scheduledGoalMarketerIds(
                                    versions,
                                    month,
                                )}
                            />
                        ))}
                        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-white px-4 py-3.5 ring-1 ring-zinc-200">
                            <ArrowPathIcon
                                aria-hidden="true"
                                className="size-4 shrink-0 text-gray-500"
                            />
                            <p className="min-w-0 flex-1 text-sm text-gray-600">
                                Goals repeat each month until you set new ones.
                            </p>
                            <SecondaryButtonLink to={newGoalsPath(nextMonth)}>
                                Set future goals
                            </SecondaryButtonLink>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export const ManageMarketerGoals: React.FC = () => {
    const currentMonth = currentYearMonth(useNow());

    return (
        <ManageGoalsPage
            title="Manage goals"
            backTo={marketerGoalsPath}
            backLabel="Goal attainment">
            {data => <GoalScheduleList data={data} currentMonth={currentMonth} />}
        </ManageGoalsPage>
    );
};

export const EditMarketerGoals: React.FC = () => {
    const { month } = useParams<{ month: string }>();

    if (!isGoalMonth(month)) {
        return <Redirect to={manageGoalsPath} />;
    }

    return (
        <ManageGoalsPage
            title={`Set goals for ${ymLabel(month)}`}
            backTo={manageGoalsPath}
            backLabel="Manage goals">
            {data => (
                <GoalDraftView
                    key={month}
                    regionId={data.regionId}
                    versions={data.versions}
                    marketerUserIds={data.marketerUserIds}
                    deactivatedMarketerUserIds={data.deactivatedMarketerUserIds}
                    sections={data.sections}
                    month={month}
                    monthControl={null}
                    userName={data.userName}
                />
            )}
        </ManageGoalsPage>
    );
};

export const NewMarketerGoals: React.FC = () => {
    const { month } = useParams<{ month: string }>();
    const nextMonth = ymAddMonths(currentYearMonth(useNow()), 1);

    if (!isGoalMonth(month) || month < nextMonth) {
        return <Redirect to={newGoalsPath(nextMonth)} />;
    }

    return (
        <ManageGoalsPage
            title={`Set goals for ${ymLabel(month)}`}
            backTo={manageGoalsPath}
            backLabel="Manage goals">
            {data => (
                <GoalDraftView
                    key="new"
                    regionId={data.regionId}
                    versions={data.versions}
                    marketerUserIds={data.marketerUserIds}
                    deactivatedMarketerUserIds={data.deactivatedMarketerUserIds}
                    sections={data.sections}
                    month={month}
                    monthControl={
                        <ApplyStartingMonth
                            versions={data.versions}
                            firstMonth={nextMonth}
                            month={month}
                        />
                    }
                    userName={data.userName}
                />
            )}
        </ManageGoalsPage>
    );
};
